import { useEffect } from 'react';

/**
 * S72 — ONE shared "close on click-outside + Escape" behaviour for every
 * custom dropdown, menu, filter panel and popover on the platform.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useDismissable(ref, isOpen, () => setOpen(false));
 *   return <div ref={ref}> ...trigger + panel... </div>;
 *
 * - `ref` must wrap BOTH the trigger and the panel, so clicking the trigger to
 *   toggle, or clicking inside the open panel, never counts as "outside".
 * - Only active while `isOpen` is true (no listeners otherwise).
 * - Does NOT change how the control opens, or what it does when open — only how
 *   it closes. Radix/shadcn primitives (DropdownMenu, Popover, Select, Dialog)
 *   already implement this; this hook is for the hand-rolled ones.
 * - Uses `mousedown`/`touchstart` (not `click`) so it closes before the next
 *   interaction, and `Escape` on keydown.
 */
export default function useDismissable(ref, isOpen, onDismiss) {
  useEffect(() => {
    if (!isOpen || typeof onDismiss !== 'function') return undefined;

    const handlePointer = (e) => {
      const el = ref?.current;
      if (el && !el.contains(e.target)) onDismiss();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') onDismiss();
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [ref, isOpen, onDismiss]);
}
